/**
 * @module app.PhotoService
 */
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStyle from 'ol/style/Style.js';
import appBase from './index.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';
import olLayerVector from 'ol/layer/Vector.js';
import TileGrid from 'ol/tilegrid/TileGrid';
import olSourceVector from 'ol/source/Vector.js';
import olSourceCluster from 'ol/source/Cluster.js';
import {LAND_LAYER_TO_PSEUDO_GROUP_LAYER, LAND_TO_LAYER, MAP_PROJECTION, Z_INDEX} from "./constants";
import {tile} from "ol/loadingstrategy";
import {isWinterLayer} from "./utils";

class PhotoService {
  /**
   * @param {string} apiUrl api base url
   * @ngInject
   */
  constructor(apiUrl) {

    /**
     * @type {string}
     * @private
     */
    this.photoLayerDataUrl_ = `${apiUrl}4/photo`;

    /**
     * @type {?ol.Map}
     * @private
     */
    this.map_;

    /**
     * @type {Array.<string>}
     * @private
     */
    this.activeLands_ = [];

    /**
     * @type {boolean}
     * @private
     */
    this.visible_ = false;

    /**
     * @type {ol.format.GeoJSON}
     * @private
     */
    this.format_ = new olFormatGeoJSON({
      dataProjection: MAP_PROJECTION
    });

    /**
     * Minimum resolution where layer is visible (inclusive)
     * @type {number}
     * @private
     */
    this.minResolution_ = 1;

    /**
     * Maximum resolution where layer is visible (exclusive)
     * @type {number}
     * @private
     */
    this.maxResolution_ = 10.001; // last visible = 10

    const tileGrid = new TileGrid({
      extent: MAP_PROJECTION.getExtent(),
      resolutions: [10],
      tileSize: 1024
    });
    const tileGridLoadingStrategy = tile(tileGrid);

    /**
     * @type {ol.source.Vector}
     * @private
     */
    const photoLayerSource = new olSourceVector({
      format: this.format_,
      strategy: tileGridLoadingStrategy,
      url: extent => `${this.photoLayerDataUrl_}?bbox=${extent.join(',')}`
    });

    this.clusterSource_ = new olSourceCluster({
      distance: 40,
      source: photoLayerSource,
      geometryFunction: feature => {
        const lands = feature.get('lands');
        // don't display if land is not active
        if (!lands.some((land) => this.activeLands_.some(activeLand => activeLand === land))) {
          return null;
        }
        return feature.getGeometry();
      }
    });

    // Change lands property to an array
    photoLayerSource.on('addfeature', (event) => {
      const lands = event.feature.get('lands').split(',');
      const renamedLands = lands.map(land => LAND_TO_LAYER[land]);
      event.feature.set('lands', renamedLands, true);
    });

    /**
     * @type {ol.layer.Vector}
     * @private
     */
    this.photoLayer_ = new olLayerVector({
      'name': 'photo layer',
      'cesiumIgnore': true,
      type: 'photo_layer',
      source: this.clusterSource_,
      visible: false,
      zIndex: Z_INDEX.PHOTOS,
      minResolution: this.minResolution_,
      maxResolution: this.maxResolution_,
      style: new olStyleStyle({
        image: new olStyleIcon({
          src: 'img/photos/camera.svg',
          imgSize: [24, 15],
        })
      })
    });
  }

  /**
   * @param {boolean} visible
   * @export
   */
  setVisible(visible) {
    this.visible_ = visible;
    this.photoLayer_.setVisible(visible);
  }

  /**
   * @param {!ol.Map} map
   * @param {!Object.<string, boolean>} wmsLayers
   * @param {string} activeSeason
   * @export
   */
  initialize(map, wmsLayers, activeSeason) {
    this.map_ = map;
    this.updateActiveLands(wmsLayers, activeSeason);
    this.map_.addLayer(this.photoLayer_);
  }

  /**
   * @param {!Object.<string, boolean>} wmsLayers
   * @param {string} activeSeason
   * @export
   */
  updateActiveLands(wmsLayers, activeSeason) {
    // create a array of names of active groups (=lands)
    const enabledLayers = Object.keys(wmsLayers).filter(key => wmsLayers[key]);
    const uniqueMappedLayers = [...new Set(enabledLayers.map((layer) => LAND_LAYER_TO_PSEUDO_GROUP_LAYER[layer]))];
    const isWinter = activeSeason === 'winter';
    this.activeLands_ = uniqueMappedLayers.filter(land => land && isWinterLayer(land.toLowerCase()) === isWinter);
    this.clusterSource_.refresh();
  }
}

export default appBase.service('appPhotoService', PhotoService);
